import type { SubmitTurnResponse } from './harness.types';

/** What a turn submission settles with once the backend has accepted it. */
export interface AcceptedTurn {
  /** Backend identity when the POST echoes it; the client-minted id otherwise. */
  turnId: string;
  userMessageIds?: readonly string[];
}

/** Why a started operation never reached a stream/snapshot terminal. */
export type TurnAbandonReason =
  | 'submit_rejected'
  | 'run_changed'
  | 'unmounted'
  | 'handed_off'
  | 'withdrawn';

/**
 * `accepted` rejection for a turn the client abandoned on purpose or by
 * lifecycle, not a submission failure. Failure reactions must ignore the
 * silent reasons; `submit_rejected` stays a visible error via the original
 * POST/overlap/permission Error.
 */
export class TurnOperationAbandonedError extends Error {
  readonly reason: TurnAbandonReason;

  constructor(reason: TurnAbandonReason) {
    super(`Turn abandoned: ${reason}`);
    this.name = 'TurnOperationAbandonedError';
    this.reason = reason;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Intentional / lifecycle abandonment — not a send failure. */
export const isSilentTurnAbandonment = (error: unknown): boolean =>
  error instanceof TurnOperationAbandonedError && error.reason !== 'submit_rejected';

/** Exactly-once settlement of a started turn. Never rejects. */
export type TurnTerminalOutcome =
  | { kind: 'completed'; turnId: string }
  | { kind: 'cancelled'; turnId: string }
  | { kind: 'failed'; turnId: string }
  | { kind: 'snapshot'; turnId: null }
  | { kind: 'abandoned'; turnId: string | null; reason: TurnAbandonReason };

/**
 * One client-minted turn from register-before-POST through its terminal.
 * `accepted` rejects on pre-POST refusal / overlap / POST failure.
 * `terminal` resolves exactly once and never rejects.
 */
export interface TurnOperation {
  readonly turnId: string;
  readonly accepted: Promise<AcceptedTurn>;
  readonly terminal: Promise<TurnTerminalOutcome>;
}

/** A turn POST echo from a backend that speaks the identity contract. */
export type IdentityEchoTurnResponse = SubmitTurnResponse & {
  user_message_ids: string[];
};

/**
 * Whether the turn POST echoed per-message artifact ids. A backend without the
 * identity contract ignores a client-minted `turn_id`, mints its own and omits
 * the echo, so this is the only reliable discriminator — and an empty echo is
 * still an echo.
 */
export const hasIdentityEcho = (
  response: SubmitTurnResponse | null | undefined
): response is IdentityEchoTurnResponse => Array.isArray(response?.user_message_ids);

/**
 * The ONE id this client mints: 32 lowercase hex, dashless, minted at ENQUEUE so
 * a queued message carries identity before dispatch. `randomUUID` needs a secure
 * context; the byte fallback does not, and a send may not fail over plain http.
 */
export const mintTurnId = (): string => {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid.replace(/-/g, '');
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
